import React from 'react';
import { Link } from 'react-router-dom';
import { Map, Package, Users, Truck, Database, UserCog } from 'lucide-react';
import useAppStore from '../store';
import { startOfDay, endOfDay, parseISO, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import SMSCounter from '../components/SMSCounter';

const HomePage: React.FC = () => {
  const { tasks = [], drivers = [], clients = [], dumpsters = [], currentUser } = useAppStore();
  
  // Get today's start and end times
  const today = new Date();
  const todayStart = startOfDay(today);
  const todayEnd = endOfDay(today);
  
  // Count stats with optional chaining
  const pendingTasks = tasks?.filter(task => task.status === 'pending')?.length ?? 0;
  const inProgressTasks = tasks?.filter(task => task.status === 'in-progress')?.length ?? 0;
  
  // Filter completed tasks for today only
  const todayCompletedTasks = tasks?.filter(task => {
    // Check if task is completed
    if (task.status !== 'completed') return false;
    
    // Get the completion date (endTime if available, otherwise date)
    const completionDate = task.endTime ? parseISO(task.endTime) : parseISO(task.date);
    
    // Check if completion date is within today's range
    return completionDate >= todayStart && completionDate <= todayEnd;
  });
  
  // Count of today's completed tasks
  const completedTasks = todayCompletedTasks?.length ?? 0;
  
  // Sort today's completed tasks by endTime (most recent first)
  const recentCompletedTasks = [...todayCompletedTasks]
    .sort((a, b) => {
      const dateA = a.endTime ? new Date(a.endTime) : new Date(a.date);
      const dateB = b.endTime ? new Date(b.endTime) : new Date(b.date);
      return dateB.getTime() - dateA.getTime();
    })
    .slice(0, 5); // Get only the 5 most recent tasks
  
  const activeDrivers = drivers?.filter(driver => driver.isOnline === true)?.length ?? 0;
  
  const StatCard: React.FC<{
    title: string;
    value: number;
    icon: React.ReactNode;
    color: string;
    link: string;
  }> = ({ title, value, icon, color, link }) => (
    <Link to={link} className="bg-white rounded-lg shadow p-6 flex items-center hover:shadow-md transition-shadow">
      <div className={`${color} rounded-full p-3 mr-4`}>
        {icon}
      </div>
      <div>
        <h3 className="text-gray-500 text-sm">{title}</h3>
        <p className="text-2xl font-bold">{value}</p>
      </div>
    </Link>
  );
  
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Tâches en attente"
          value={pendingTasks}
          icon={<Package className="h-6 w-6 text-blue-800" />}
          color="bg-blue-100"
          link="/tasks"
        />
        
        <StatCard
          title="Tâches en cours"
          value={inProgressTasks}
          icon={<Package className="h-6 w-6 text-yellow-800" />}
          color="bg-yellow-100"
          link="/tasks"
        />
        
        <StatCard
          title="Tâches terminées aujourd'hui"
          value={completedTasks}
          icon={<Package className="h-6 w-6 text-green-800" />}
          color="bg-green-100"
          link="/tasks"
        />
        
        <StatCard
          title="Chauffeurs actifs"
          value={activeDrivers}
          icon={<Truck className="h-6 w-6 text-purple-800" />}
          color="bg-purple-100"
          link="/map"
        />
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {currentUser?.role === 'admin' && (
          <SMSCounter className="lg:col-span-2" />
        )}

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Tâches récentes</h2>
            <Link to="/tasks" className="text-blue-800 hover:underline">Voir tout</Link>
          </div>
          
          <div className="space-y-4">
            {recentCompletedTasks.length > 0 ? (
              recentCompletedTasks.map(task => {
                const driver = drivers?.find(d => d.id === task.driverId);
                const client = clients?.find(c => c.id === task.clientId);
                
                return (
                  <div key={task.id} className="border-b pb-4 last:border-0">
                    <div className="flex flex-col sm:flex-row sm:justify-between">
                      <div>
                        <p className="font-medium">{client?.companyName}</p>
                        <p className="text-sm text-gray-600">
                          Chauffeur: {driver?.firstName} {driver?.lastName}
                        </p>
                      </div>
                      <div className="text-left sm:text-right mt-2 sm:mt-0">
                        <p className="text-sm">
                          {task.endTime 
                            ? format(new Date(task.endTime), 'dd/MM/yyyy HH:mm', { locale: fr })
                            : format(new Date(task.date), 'dd/MM/yyyy HH:mm', { locale: fr })}
                        </p>
                        <span className="inline-block px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                          Terminée
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center text-gray-500 py-4">
                Aucune tâche terminée aujourd'hui
              </div>
            )}
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Accès rapides</h2>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <Link to="/map" className="bg-blue-100 rounded-lg p-4 flex flex-col items-center justify-center hover:bg-blue-200 transition-colors">
              <Map className="h-8 w-8 text-blue-800 mb-2" />
              <span className="font-medium">Carte</span>
            </Link>
            
            <Link to="/tasks" className="bg-yellow-100 rounded-lg p-4 flex flex-col items-center justify-center hover:bg-yellow-200 transition-colors">
              <Package className="h-8 w-8 text-yellow-800 mb-2" />
              <span className="font-medium">Tâches</span>
            </Link>
            
            <Link to="/clients" className="bg-green-100 rounded-lg p-4 flex flex-col items-center justify-center hover:bg-green-200 transition-colors">
              <Users className="h-8 w-8 text-green-800 mb-2" />
              <span className="font-medium">Clients</span>
            </Link>
            
            {currentUser?.role === 'admin' && (
              <>
                <Link to="/depots" className="bg-gray-100 rounded-lg p-4 flex flex-col items-center justify-center hover:bg-gray-200 transition-colors">
                  <Database className="h-8 w-8 text-gray-800 mb-2" />
                  <span className="font-medium">Dépôts</span>
                </Link>
                
                <Link to="/users" className="bg-red-100 rounded-lg p-4 flex flex-col items-center justify-center hover:bg-red-200 transition-colors">
                  <UserCog className="h-8 w-8 text-red-800 mb-2" />
                  <span className="font-medium">Utilisateurs</span>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow p-6 overflow-x-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Bennes déployées</h2>
          <Link to="/map" className="text-blue-800 hover:underline">Voir sur la carte</Link>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Numéro</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Date de dépôt</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Chauffeur</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {dumpsters?.filter(d => d.clientId)?.map(dumpster => {
                const client = clients?.find(c => c.id === dumpster.clientId);
                const driver = drivers?.find(d => d.id === dumpster.driverId);
                
                return (
                  <tr key={dumpster.id}>
                    <td className="px-6 py-4 whitespace-nowrap">{dumpster.number}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{dumpster.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{client?.companyName || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                      {dumpster.dropDate ? format(new Date(dumpster.dropDate), 'dd/MM/yyyy', { locale: fr }) : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                      {driver ? `${driver.firstName} ${driver.lastName}` : 'N/A'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      
      <div className="text-right text-sm text-gray-500">
        v2.9.0
      </div>
    </div>
  );
};

export default HomePage;